import { createFileRoute } from "@tanstack/react-router";
import {
  Building,
  Cpu,
  Sparkles,
  FileCheck,
  FileSearch,
  Link as LinkIcon,
  Monitor,
  ScrollText,
  LockKeyhole,
  ShieldCheck,
  Users,
  Package,
} from "lucide-react";
import { SecondaryNav } from "@client/components/secondary-nav";
import { useAuthState } from "@tubo/store";
import { useGetPublicationReviewAccess } from "@tubo/api-models/hooks/publication-api.hooks";
import { usePermissions } from "@client/hooks/use-permissions";
import { useNavPermissions } from "@client/hooks/use-nav-permissions";

export const Route = createFileRoute("/_app/tenant-settings")({
  component: TSettingsLayout,
});

const tSettingsItems = [
  {
    title: "团队信息",
    to: "/tenant-settings/tenant-info" as const,
    icon: Building,
    permission: "team-info",
  },
  {
    title: "团队管理",
    to: "/tenant-settings/users" as const,
    icon: Users,
    permissions: ["team-users", "team-departments", "team-roles"],
  },
  {
    title: "权限管理",
    to: "/tenant-settings/permissions" as const,
    icon: ShieldCheck,
    permission: "team-share-governance",
  },
  {
    title: "文档安全",
    to: "/tenant-settings/document-security" as const,
    icon: LockKeyhole,
    permission: "team-share-governance",
  },
  {
    title: "AI 模型",
    to: "/tenant-settings/ai-models" as const,
    icon: Sparkles,
    permission: "team-ai-models",
  },
  {
    title: "解析引擎",
    to: "/tenant-settings/parse-engines" as const,
    icon: Cpu,
    permission: "team-parse-engines",
  },
  {
    title: "文档设置",
    to: "/tenant-settings/doc-settings" as const,
    icon: FileSearch,
    permission: "team-doc-settings",
  },
  {
    title: "数据连接",
    to: "/tenant-settings/data-sources" as const,
    icon: LinkIcon,
    permission: "team-data-sources",
  },
  {
    title: "发布审核",
    to: "/tenant-settings/publications" as const,
    icon: FileCheck,
  },
  {
    title: "行业包",
    to: "/tenant-settings/industry-packs" as const,
    icon: Package,
  },
  {
    title: "审计日志",
    to: "/tenant-settings/audit-logs" as const,
    icon: ScrollText,
    permission: "team-audit-logs",
  },
  {
    title: "系统状态",
    to: "/tenant-settings/system-status" as const,
    icon: Monitor,
    permission: "team-system-status",
  },
];

function TSettingsLayout() {
  const { user } = useAuthState();
  // 二级目录较窄,头部只显示租户名称(主侧栏仍显示「团队设置」),无租户名时兜底原标题
  const tenantName = user?.tenant?.name ?? "";
  const title = tenantName || "团队设置";

  // 管理员专属入口（PF-13 配置即放行）：发布审核配置为指定审核人者可见；行业包仅管理员
  const { isTenantAdmin } = usePermissions();
  const { data: reviewAccess } = useGetPublicationReviewAccess();
  const canSeePublications = isTenantAdmin || !!reviewAccess?.canReview;

  // 后端一次返回当前用户有权访问的所有 key
  const permSet = useNavPermissions();

  const items = tSettingsItems
    .filter(
      (item) =>
        (item.to !== "/tenant-settings/publications" || canSeePublications) &&
        (item.to !== "/tenant-settings/industry-packs" || isTenantAdmin) &&
        (item.permission !== "team-share-governance" || isTenantAdmin),
    )
    .filter((item) => {
      if (item.permissions) {
        return item.permissions.some((permission) => permSet.has(permission));
      }
      if (!item.permission) return true;
      return permSet.has(item.permission);
    });

  return <SecondaryNav title={title} items={items} />;
}
